import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import WhatsAppFloat from '../components/WhatsAppFloat';
import AvaliacoesGoogle from '../components/AvaliacoesGoogle';
import AreasAtuacao from '../components/AreasAtuacao';
import CalculadoraModal from '../components/CalculadoraModal';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [calculadoraAberta, setCalculadoraAberta] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        mensagem: ''
      });
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-off-white">
      <WhatsAppFloat />
      
      {/* Header/Navigation */}
      <header className="fixed top-0 w-full bg-white shadow-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="monogram">LR</div>
              <div>
                <h1 className="brand-title text-lg md:text-xl">
                  LEANDRO RIBEIRO
                </h1>
                <p className="brand-subtitle">ADVOCACIA ESPECIALIZADA</p>
              </div>
            </div>
            <div className="hidden md:flex gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin/login')}
                className="text-oxford hover:text-champagne font-semibold"
              >
                Área Administrativa
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/cliente/login')}
                className="text-oxford hover:text-champagne font-semibold"
              >
                Área do Cliente
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section Super Premium */}
      <section className="pt-32 pb-24 px-4 hero-premium min-h-screen flex items-center">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <div className="text-white space-y-8 animate-fade-in-up">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight text-white">
                  Seus Direitos Trabalhistas
                  <span className="block text-champagne mt-3">com Precisão Jurídica</span>
                </h1>
                <p className="text-xl md:text-2xl lg:text-3xl text-white font-light leading-relaxed mb-8 opacity-95">
                  Atuação técnica e estratégica para trabalhadores que precisam de defesa séria e especializada.
                </p>
                <p className="text-lg md:text-xl text-white leading-relaxed opacity-90">
                  Foi demitido? Quer pedir demissão? Trabalha além da jornada? 
                  <span className="text-champagne font-semibold"> Consulte seus direitos e valores que pode ter a receber.</span>
                </p>
              </div>

              {/* 4 Botões de Calculadora */}
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={() => setCalculadoraAberta('rescisao')}
                  size="lg" 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-champagne hover:border-champagne hover:text-oxford transition-all duration-300 py-6 font-semibold"
                  data-testid="calc-rescisao-btn"
                >
                  Calcular Rescisão
                </Button>
                <Button 
                  onClick={() => setCalculadoraAberta('ferias')}
                  size="lg" 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-champagne hover:border-champagne hover:text-oxford transition-all duration-300 py-6 font-semibold"
                  data-testid="calc-ferias-btn"
                >
                  Calcular Férias
                </Button>
                <Button 
                  onClick={() => setCalculadoraAberta('decimo')}
                  size="lg" 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-champagne hover:border-champagne hover:text-oxford transition-all duration-300 py-6 font-semibold"
                  data-testid="calc-decimo-btn"
                >
                  Calcular 13º Salário
                </Button>
                <Button 
                  onClick={() => setCalculadoraAberta('horas')}
                  size="lg" 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-champagne hover:border-champagne hover:text-oxford transition-all duration-300 py-6 font-semibold"
                  data-testid="calc-horas-btn"
                >
                  Calcular Horas Extras
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/20">
                <div className="flex items-center gap-3">
                  <div className="bg-champagne/20 p-2 rounded-full">
                    <CheckCircle className="w-5 h-5 text-champagne" />
                  </div>
                  <span className="text-white text-sm font-medium">OAB/GO 77.457</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-champagne/20 p-2 rounded-full">
                    <Shield className="w-5 h-5 text-champagne" />
                  </div>
                  <span className="text-white text-sm font-medium">Atendimento em todo Brasil</span>
                </div>
              </div>
            </div>

            {/* Right Side - Professional Photo */}
            <div className="flex justify-center lg:justify-end animate-fade-in-up">
              <img 
                src="https://customer-assets.emergentagent.com/job_ability-check-18/artifacts/7e1lr8np_14.png"
                alt="Dr. Leandro Ribeiro - Advogado Trabalhista"
                className="hero-image-professional"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Calculadora Modal */}
      {calculadoraAberta && (
        <CalculadoraModal 
          tipo={calculadoraAberta}
          isOpen={!!calculadoraAberta}
          onClose={() => setCalculadoraAberta(null)}
        />
      )}

      {/* Avaliações Google */}
      <AvaliacoesGoogle />

      {/* Áreas de Atuação com Modais */}
      <AreasAtuacao />

      {/* Sobre */}
      <section className="py-20 px-4 bg-off-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="w-20 h-1 bg-champagne mb-6"></div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-oxford mb-6 leading-tight">
                Advocacia Trabalhista de Excelência
              </h2>
              <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                Escritório especializado em Direito do Trabalho, oferecendo assessoria 
                jurídica completa para trabalhadores em todo o Brasil.
              </p>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Com atuação pautada pela ética, transparência e compromisso com os 
                direitos dos trabalhadores, buscamos sempre as melhores soluções jurídicas 
                para cada caso.
              </p>
              <div className="flex flex-col gap-4 mt-8">
                <div className="flex items-center gap-3">
                  <div className="bg-champagne/20 p-2 rounded-full">
                    <CheckCircle className="w-6 h-6 text-champagne" />
                  </div>
                  <span className="font-semibold text-oxford text-lg">Atendimento personalizado</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-champagne/20 p-2 rounded-full">
                    <CheckCircle className="w-6 h-6 text-champagne" />
                  </div>
                  <span className="font-semibold text-oxford text-lg">Atuação em todo Brasil</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-champagne/20 p-2 rounded-full">
                    <CheckCircle className="w-6 h-6 text-champagne" />
                  </div>
                  <span className="font-semibold text-oxford text-lg">Acompanhamento processual completo</span>
                </div>
              </div>
            </div>
            <div className="card-glass p-10 rounded-2xl shadow-2xl animate-fade-in-up">
              <h3 className="font-serif text-3xl font-bold text-oxford mb-8">
                Atendimento
              </h3>
              <div className="space-y-6 text-gray-700">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-champagne flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-oxford text-lg">Presencial:</p>
                    <p className="text-lg">Goiânia - GO</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-champagne flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-oxford text-lg">Online:</p>
                    <p className="text-lg">Atendimento em todo Brasil</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulário de Contato */}
      <section id="contato" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-oxford mb-4">
              Entre em Contato
            </h2>
            <div className="w-32 h-1 bg-champagne mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg">
              Preencha o formulário abaixo e nossa equipe entrará em contato
            </p>
          </div>

          <Card className="card-glass shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-oxford font-semibold mb-2 text-lg">Nome Completo *</label>
                  <Input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    placeholder="Seu nome completo"
                    className="input-premium text-lg py-6"
                    data-testid="nome-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-oxford font-semibold mb-2 text-lg">E-mail *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="seu@email.com"
                      className="input-premium text-lg py-6"
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-oxford font-semibold mb-2 text-lg">Telefone/WhatsApp *</label>
                    <Input
                      type="tel"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      required
                      placeholder="(62) 9 9999-9999"
                      className="input-premium text-lg py-6"
                      data-testid="telefone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-oxford font-semibold mb-2 text-lg">Mensagem *</label>
                  <Textarea
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    placeholder="Descreva brevemente seu caso ou dúvida..."
                    className="input-premium text-lg"
                    data-testid="mensagem-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="btn-champagne w-full py-7 text-lg font-bold"
                  data-testid="enviar-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar Mensagem'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-oxford text-white py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="monogram text-3xl">LR</div>
                <div>
                  <h3 className="brand-title text-white text-base">LEANDRO RIBEIRO</h3>
                  <p className="brand-subtitle">ADVOCACIA ESPECIALIZADA</p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed">Direito do Trabalho</p>
              <p className="text-champagne font-semibold mt-3">OAB/GO 77.457</p>
            </div>

            <div>
              <h4 className="font-serif font-semibold text-xl mb-6 text-champagne">Contato</h4>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-champagne" />
                  <a href="https://wa.me/5562996951404" className="hover:text-champagne transition">
                    (62) 9 9695-1404
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-champagne" />
                  <a href="mailto:leandro@leandroribeiro.adv.br" className="hover:text-champagne transition">
                    leandro@leandroribeiro.adv.br
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-champagne" />
                  <span>Goiânia - GO</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-serif font-semibold text-xl mb-6 text-champagne">Horário</h4>
              <p className="text-gray-300 mb-2">Segunda a Sexta</p>
              <p className="text-gray-300 mb-4">09:00 - 18:00</p>
              <p className="text-champagne font-semibold">Atendimento online em todo Brasil</p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p className="mb-2">© {new Date().getFullYear()} Leandro Ribeiro Advocacia & Consultoria. Todos os direitos reservados.</p>
            <p className="text-sm">Advocacia em conformidade com a Resolução 205/2021 da OAB</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
